import getWindow from 'Utils/get-window';

/**
 * IAB consent readers shared by the ads stack (flint header bidding and
 * ads identity). The Sourcepoint CMP renders the __tcfapi/__uspapi/__gpp
 * stubs in the document head on every page (see
 * template/partials/external/sourcepoint.tsx); the stubs queue callbacks
 * until the CMP has loaded, so every read is wrapped in a short timeout to
 * guarantee the ad path is never stalled by a CMP that fails to load.
 */

// How long to wait on the CMP consent APIs before giving up (they answer
// synchronously once the CMP has loaded; the stubs queue callbacks until
// then).
export const CONSENT_TIMEOUT_MS = 300;

export type TcData = {
  tcString?: string;
  gdprApplies?: boolean;
  purpose?: {
    consents?: Record<string, boolean>;
  };
};
export type UspData = { uspString?: string };
export type GppData = { gppString?: string; applicableSections?: number[] };

type ConsentWindow = {
  __tcfapi?: (
    command: string,
    version: number,
    callback: (data: TcData | null, success: boolean) => void,
  ) => void;
  __uspapi?: (
    command: string,
    version: number,
    callback: (data: UspData | null, success: boolean) => void,
  ) => void;
  __gpp?: (
    command: string,
    callback: (data: GppData | null, success: boolean) => void,
  ) => void;
};

const getConsentWindow = (): ConsentWindow =>
  getWindow() as unknown as ConsentWindow;

// Resolve a CMP callback-style API with a timeout so a stub whose CMP never
// loads cannot stall the caller. The timer is cleared when the consent
// promise wins the race, so fast CMP responses don't leave timers queued.
const withConsentTimeout = <T>(
  promise: Promise<T | null>,
): Promise<T | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), CONSENT_TIMEOUT_MS);
    }),
  ]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  });
};

/**
 * Full IAB TCF consent data (GDPR), via the CMP's __tcfapi: the consent
 * string plus gdprApplies and per-purpose consents, for callers (ads
 * identity) that need more than the string itself. Resolves null when the
 * API is missing or the CMP has not answered within the timeout.
 */
export const getTcfConsentData = (): Promise<TcData | null> =>
  withConsentTimeout(
    new Promise<TcData | null>((resolve) => {
      const api = getConsentWindow().__tcfapi;
      if (typeof api !== 'function') {
        resolve(null);
        return;
      }
      try {
        api('getTCData', 2, (data, success) => {
          resolve((success && data) || null);
        });
      } catch (_) {
        resolve(null);
      }
    }),
  );

/** IAB TCF consent string (GDPR), via the CMP's __tcfapi. */
export const getTcfConsentString = (): Promise<string | null> =>
  getTcfConsentData().then((data) => data?.tcString || null);

/** IAB US Privacy string (CCPA), via the CMP's __uspapi. */
export const getUspConsentString = (): Promise<string | null> =>
  withConsentTimeout(
    new Promise<string | null>((resolve) => {
      const api = getConsentWindow().__uspapi;
      if (typeof api !== 'function') {
        resolve(null);
        return;
      }
      try {
        api('getUSPData', 1, (data, success) => {
          resolve((success && data?.uspString) || null);
        });
      } catch (_) {
        resolve(null);
      }
    }),
  );

/** IAB GPP string + applicable section ids, via the CMP's __gpp. */
export const getGppConsent = (): Promise<GppData | null> =>
  withConsentTimeout(
    new Promise<GppData | null>((resolve) => {
      const api = getConsentWindow().__gpp;
      if (typeof api !== 'function') {
        resolve(null);
        return;
      }
      try {
        api('getGPPData', (data, success) => {
          resolve((success && data) || null);
        });
      } catch (_) {
        resolve(null);
      }
    }),
  );
